import { useState, useMemo, useEffect } from "react";
import Icon from "../components/Icon";
import { clicable } from "../components/ClicableTeclado";
import { buildFestivos, esFinde, diasLaborables } from "../utils";

const DAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const DAYS_INI = ["L", "M", "X", "J", "V", "S", "D"];
const MONTHS_ES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function isoDate(d) {
  if (!d) return null;
  if (d instanceof Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return String(d).slice(0, 10);
}

// Returns array of {date, isCurrentMonth} for a 6-week grid starting on Monday
function buildGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  // getDay(): 0=Sun,1=Mon,...,6=Sat → adjust so Mon=0
  const startOffset = (firstDay.getDay() + 6) % 7;
  const start = new Date(firstDay);
  start.setDate(1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push({ date: d, isCurrentMonth: d.getMonth() === month });
  }
  return cells;
}

// Un evento con rango ocupa todos los días entre fecha y fecha_fin
function diasDelEvento(ev) {
  const ini = isoDate(ev.fecha);
  if (!ini) return [];
  const fin = ev.fecha_fin ? isoDate(ev.fecha_fin) : ini;
  if (fin === ini) return [ini];
  const out = [];
  const d = new Date(ini + "T00:00:00");
  const end = new Date(fin + "T00:00:00");
  for (let i = 0; d <= end && i < 400; i++) {
    out.push(isoDate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}


// Rejilla de un mes para la vista anual: semanas de lunes a domingo con huecos
function gridMes(year, month) {
  const primero = new Date(year, month, 1);
  const offset  = (primero.getDay() + 6) % 7;
  const total   = new Date(year, month + 1, 0).getDate();
  const celdas  = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= total; d++) celdas.push(isoDate(new Date(year, month, d)));
  while (celdas.length % 7 !== 0) celdas.push(null);
  return celdas;
}

// Nombre accesible de una celda de día: fecha larga y qué contiene (A07).
export function etiquetaDia(iso, { festivo, nOut = 0, nAg = 0, nCal = 0 }) {
  const [y, m, d] = iso.split("-").map(Number);
  const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
  return [`${d} de ${MONTHS_ES[m - 1].toLowerCase()} de ${y}`,
    nOut > 0 && plural(nOut, "evento de Outlook", "eventos de Outlook"),
    nCal > 0 && plural(nCal, "evento de calendario", "eventos de calendario"),
    nAg > 0 && plural(nAg, "entrada del Cuaderno", "entradas del Cuaderno"),
    festivo && `festivo: ${festivo}`].filter(Boolean).join(", ");
}

export default function CalendarPage({
  outlookEvents = [],
  agenda = [],
  calendarios = [],
  eventosCal = [],
  currentUser,
  onNuevoEvento,
  onEditarEvento,
  onGestionarCalendarios,
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = isoDate(today);

  const [year,  setYear ] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [isMob, setIsMob] = useState(() => window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(false);
  const [vista, setVista] = useState("anio");  // "mes" | "anio" — se abre en el año
  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Filters
  const [showOutlook, setShowOutlook] = useState(true);
  const [showAgenda,  setShowAgenda ] = useState(true);
  // Calendarios ocultos: se guarda lo oculto, así los nuevos aparecen visibles solos
  const [ocultos, setOcultos] = useState(() => new Set());
  const visible = id => !ocultos.has(String(id));
  const toggleCal = id => setOcultos(p => {
    const n = new Set(p);
    const k = String(id);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  // En la vista de año las flechas mueven el año entero: cambiar de mes ahí no
  // altera nada de lo que se ve.
  const retroceder = () => {
    if (vista === "anio") { setYear(y => y - 1); setSelectedDay(null); return; }
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const avanzar = () => {
    if (vista === "anio") { setYear(y => y + 1); setSelectedDay(null); return; }
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
    setSelectedDay(todayStr);
  };

  const cells = useMemo(() => buildGrid(year, month), [year, month]);

  // Index data by date string
  const outlookByDate = useMemo(() => {
    const idx = {};
    if (!showOutlook) return idx;
    outlookEvents.forEach(e => {
      const d = e.start_datetime ? isoDate(new Date(e.start_datetime)) : null;
      if (!d) return;
      if (!idx[d]) idx[d] = [];
      idx[d].push(e);
    });
    return idx;
  }, [outlookEvents, showOutlook]);

  const agendaByDate = useMemo(() => {
    const idx = {};
    if (!showAgenda) return idx;
    agenda.forEach(a => {
      const d = isoDate(a.entry_date);
      if (!d) return;
      if (!idx[d]) idx[d] = [];
      idx[d].push(a);
    });
    return idx;
  }, [agenda, showAgenda]);

  // Eventos de calendario visibles, indexados por día (los rangos ocupan varios)
  const calByDate = useMemo(() => {
    const idx = {};
    eventosCal.forEach(ev => {
      if (!visible(ev.calendario_id)) return;
      diasDelEvento(ev).forEach(d => {
        if (!idx[d]) idx[d] = [];
        idx[d].push(ev);
      });
    });
    return idx;
  }, [eventosCal, ocultos]); // eslint-disable-line

  // Los festivos salen de TODOS los calendarios, se filtren o no en la vista:
  // ocultar un calendario cambia lo que se ve, no los días que se trabajan.
  const festivos = useMemo(() => buildFestivos(eventosCal), [eventosCal]);
  const laborables = useMemo(() => diasLaborables(year, month, festivos), [year, month, festivos]);
  const laborablesAnio = useMemo(
    () => Array.from({ length: 12 }, (_, m) => diasLaborables(year, m, festivos)).reduce((a, b) => a + b, 0),
    [year, festivos]);

  // Selected day data
  const selStr    = selectedDay;
  const selOutlook = selStr ? (outlookByDate[selStr] || []) : [];
  const selAgenda  = selStr ? (agendaByDate[selStr]  || []) : [];
  const selCal     = selStr ? (calByDate[selStr]     || []) : [];
  const selDate    = selStr ? new Date(selStr + "T00:00:00") : null;

  const puedeCrear = calendarios.some(c => c.can_edit && !c.archived);

  const CalToggles = ({ compact }) => (
    <>
      {calendarios.filter(c => !c.archived).map(c => (
        <label key={c.id} title={c.empresa ? "Calendario de empresa" : "Calendario personal"}
          style={{display:"flex",alignItems:"center",gap:compact?6:8,cursor:"pointer",
            padding:compact?0:"5px 0",fontSize:12,color:"var(--text2)"}}>
          <input type="checkbox" checked={visible(c.id)} onChange={()=>toggleCal(c.id)}
            style={{accentColor:c.color||"#94a3b8",width:14,height:14,cursor:"pointer"}}/>
          <span style={{fontWeight:visible(c.id)?600:400,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {c.name}
          </span>
        </label>
      ))}
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: 0 }}>

      {/* ── Panel de filtros (móvil: colapsable; escritorio: aside lateral) ── */}
      {isMob && showFilters && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", marginBottom: 10, flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[["Outlook", showOutlook, setShowOutlook, "#0078d4"], ["Cuaderno", showAgenda, setShowAgenda, "#8b5cf6"]].map(([label, val, setter, color]) => (
              <label key={label} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "var(--text2)" }}>
                <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} style={{ accentColor: color, width: 14, height: 14 }}/>
                <span style={{ fontWeight: val ? 600 : 400 }}>{label}</span>
              </label>
            ))}
            <CalToggles compact/>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
      {/* ── Left: filter panel (escritorio) ── */}
      <aside className="cal-aside" style={{ width: 200, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
        <div style={{ background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", padding: "14px 14px 10px" }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 10 }}>Mostrar</div>
          {[
            ["Outlook", showOutlook, setShowOutlook, "#0078d4"],
            ["Cuaderno", showAgenda, setShowAgenda, "#8b5cf6"],
          ].map(([label, val, setter, color]) => (
            <label key={label} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "5px 0", fontSize: 12, color: "var(--text2)" }}>
              <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} style={{ accentColor: color, width: 14, height: 14, cursor: "pointer" }}/>
              <span style={{ fontWeight: val ? 600 : 400 }}>{label}</span>
            </label>
          ))}
        </div>

        {/* ── Calendarios ── */}
        <div style={{ background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", padding: "14px 14px 10px" }}>
          <div style={{ fontWeight: 700, fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 10 }}>Calendarios</div>
          {calendarios.filter(c => !c.archived).length
            ? <CalToggles/>
            : <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.4, paddingBottom: 4 }}>Aún no hay calendarios.</div>}
          <button onClick={onGestionarCalendarios} className="btn"
            style={{ marginTop: 10, width: "100%", padding: "6px 8px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "var(--text2)", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
            <Icon name="gear" size={11} color="var(--text2)"/> Gestionar
          </button>
        </div>
      </aside>

      {/* ── Right: calendar + detail ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>

        {/* ── Month nav ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isMob && (
            <button onClick={() => setShowFilters(p => !p)} className="btn"
              style={{ padding: "5px 9px", borderRadius: 8, border: "1px solid var(--border)", background: showFilters ? "var(--accent-bg)" : "var(--surface)", color: showFilters ? "var(--accent)" : "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontFamily: "var(--font)" }}>
              <Icon name="funnel-fill" size={12} color={showFilters ? "var(--accent)" : "var(--text2)"}/>
              {showFilters ? "Ocultar" : "Filtros"}
            </button>
          )}
          <button aria-label="Periodo anterior" onClick={retroceder} className="btn"
            style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Icon name="chevron-left" size={14} color="var(--text2)"/>
          </button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "var(--text)", letterSpacing: "-.02em" }}>
              {vista === "anio" ? year : `${MONTHS_ES[month]} ${year}`}
            </div>
            <div className="m-hide" style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 1 }}>
              {vista === "anio" ? laborablesAnio : laborables} días laborables
            </div>
          </div>
          <button aria-label="Periodo siguiente" onClick={avanzar} className="btn"
            style={{ padding: "5px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Icon name="chevron-right" size={14} color="var(--text2)"/>
          </button>
          <button onClick={goToday} className="btn"
            style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", cursor: "pointer", fontSize: 11.5, fontWeight: 600, color: "var(--accent)", fontFamily: "var(--font)" }}>
            Hoy
          </button>
          <div style={{ display: "flex", gap: 3, background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 9, padding: 2 }}>
            {[["mes","Mes"],["anio","Año"]].map(([k,label]) => (
              <button key={k} onClick={() => setVista(k)} className="btn"
                style={{ padding: "4px 11px", borderRadius: 7, border: "none", cursor: "pointer",
                  fontSize: 11.5, fontFamily: "var(--font)",
                  background: vista===k ? "var(--surface)" : "transparent",
                  color: vista===k ? "var(--accent)" : "var(--text3)",
                  fontWeight: vista===k ? 700 : 500,
                  boxShadow: vista===k ? "var(--shadow-sm)" : "none" }}>
                {label}
              </button>
            ))}
          </div>
          {puedeCrear && (
            <button onClick={() => onNuevoEvento?.(selectedDay || todayStr)} className="btn"
              style={{ padding: "5px 12px", borderRadius: 8, border: "none", background: "var(--accent)", cursor: "pointer", fontSize: 11.5, fontWeight: 600, color: "var(--on-accent)", fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="plus-lg" size={11} color="#fff"/> Evento
            </button>
          )}
        </div>

        {/* ── Vista mensual ── */}
        {vista === "mes" && (
        <div style={{ flex: 1, background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* Day headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
            {DAYS_ES.map((d, di) => (
              <div key={d} style={{ padding: "8px 0", textAlign: "center", fontSize: 10.5, fontWeight: 700, color: di > 4 ? "var(--text3)" : "var(--text3)", textTransform: "uppercase", letterSpacing: ".07em", opacity: di > 4 ? .7 : 1 }}>
                {d}
              </div>
            ))}
          </div>
          {/* Grid cells */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", flex: 1, overflow: "hidden" }}>
            {cells.map(({ date, isCurrentMonth }, i) => {
              const ds       = isoDate(date);
              const isToday  = ds === todayStr;
              const isSel    = ds === selStr;
              const dayAg    = agendaByDate[ds] || [];
              const dayCal   = calByDate[ds] || [];
              const festivo  = festivos[ds] || null;
              const finde    = esFinde(ds);
              // Prioridad de fondo: seleccionado > hoy > festivo > fin de semana
              const bg = isSel   ? "var(--accent-bg)"
                       : isToday ? "rgba(0,180,216,.06)"
                       : festivo ? "rgba(239,68,68,.07)"
                       : finde   ? "var(--bg)"
                       : "transparent";

              return (
                <div key={i}
                  {...clicable(() => setSelectedDay(isSel ? null : ds), { pressed: isSel,
                    label: etiquetaDia(ds, { festivo, nOut: (outlookByDate[ds] || []).length, nAg: dayAg.length, nCal: dayCal.length }) })}
                  title={festivo || undefined}
                  style={{
                    borderRight:  (i % 7 !== 6) ? "1px solid var(--border2)" : "none",
                    borderBottom: (i < 35)       ? "1px solid var(--border2)" : "none",
                    padding: "6px 8px",
                    cursor: "pointer",
                    background: bg,
                    transition: "background .1s",
                    display: "flex", flexDirection: "column", gap: 3, minHeight: 64,
                    opacity: isCurrentMonth ? 1 : 0.3,
                    overflow: "hidden",
                  }}>
                  <div style={{
                    fontSize: 12, fontWeight: isToday ? 800 : 500,
                    width: 22, height: 22, borderRadius: "50%",
                    background: isToday ? "var(--accent)" : "transparent",
                    color: isToday ? "var(--on-accent)" : isSel ? "var(--accent)" : festivo ? "var(--red-text)" : isCurrentMonth ? (finde ? "var(--text3)" : "var(--text2)") : "var(--text3)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {date.getDate()}
                  </div>
                  {/* Nombre del festivo */}
                  {festivo && (
                    <div style={{ fontSize: 9, fontWeight: 700, color: "var(--red-text)", lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {festivo}
                    </div>
                  )}
                  {/* Eventos de calendario */}
                  {dayCal.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {dayCal.slice(0, 3).map((ev, vi) => (
                        <div key={vi} title={ev.titulo}
                          style={{ width: 7, height: 7, borderRadius: 2, background: ev.color || ev.calendario_color || "#94a3b8", flexShrink: 0 }}/>
                      ))}
                      {dayCal.length > 3 && <div style={{ fontSize: 8, color: "var(--text3)", lineHeight: "7px" }}>+{dayCal.length - 3}</div>}
                    </div>
                  )}
                  {/* Outlook dots */}
                  {(outlookByDate[ds] || []).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {(outlookByDate[ds] || []).slice(0, 3).map((e, ei) => (
                        <div key={ei} title={e.title}
                          style={{ width: 7, height: 7, borderRadius: "50%", background: "#0078d4", flexShrink: 0 }}/>
                      ))}
                      {(outlookByDate[ds] || []).length > 3 && <div style={{ fontSize: 8, color: "#0078d4", lineHeight: "7px" }}>+{(outlookByDate[ds] || []).length - 3}</div>}
                    </div>
                  )}
                  {/* Agenda dots */}
                  {dayAg.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {dayAg.slice(0, 3).map((a, ai) => (
                        <div key={ai} title={a.description || a.title || "Cuaderno"}
                          style={{ width: 6, height: 6, borderRadius: 1, background: "#8b5cf6", flexShrink: 0, transform: "rotate(45deg)", margin: 1 }}/>
                      ))}
                      {dayAg.length > 3 && <div style={{ fontSize: 8, color: "#8b5cf6", lineHeight: "7px" }}>+{dayAg.length - 3}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* ── Vista anual: los doce meses de un vistazo ── */}
        {vista === "anio" && (
          <div className="cal-anio" style={{ flex: 1, overflowY: "auto" }}>
            {MONTHS_ES.map((nombre, mi) => (
              <div key={mi} style={{ background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", padding: "9px 10px 10px" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                  <button onClick={() => { setMonth(mi); setVista("mes"); }} className="btn"
                    style={{ flex: 1, background: "none", border: "none", cursor: "pointer", padding: 0,
                      fontSize: 11.5, fontWeight: 800, color: mi === today.getMonth() && year === today.getFullYear() ? "var(--accent)" : "var(--text)",
                      fontFamily: "var(--font)", textAlign: "center" }}>
                    {nombre}
                  </button>
                  <span style={{ fontSize: 9, color: "var(--text3)" }}
                    title="Días laborables del mes">{diasLaborables(year, mi, festivos)}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
                  {DAYS_INI.map((d, di) => (
                    <div key={di} style={{ fontSize: 8.5, fontWeight: 700, color: "var(--text3)", textAlign: "center", paddingBottom: 2 }}>{d}</div>
                  ))}
                  {gridMes(year, mi).map((iso, ci) => {
                    if (!iso) return <div key={ci}/>;
                    const dia   = parseInt(iso.slice(8), 10);
                    const fest  = festivos[iso];
                    const finde = esFinde(iso);
                    const isSel = iso === selStr;
                    const nOut  = (outlookByDate[iso] || []).length;
                    const nAg   = (agendaByDate[iso]  || []).length;
                    const dayCal = calByDate[iso] || [];
                    const hayAlgo = nOut + nAg + dayCal.length > 0;

                    return (
                      <div key={ci}
                        {...clicable(() => setSelectedDay(isSel ? null : iso), { pressed: isSel,
                          label: etiquetaDia(iso, { festivo: fest, nOut, nAg, nCal: dayCal.length }) })}
                        title={[fest && `Festivo: ${fest}`,
                                nOut && `${nOut} evento(s) Outlook`,
                                dayCal.length && `${dayCal.length} en calendarios`,
                                nAg && `${nAg} entrada(s) de cuaderno`].filter(Boolean).join("\n") || undefined}
                        style={{
                          aspectRatio: "1", display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", gap: 1,
                          fontSize: 9.5, fontWeight: iso === todayStr ? 800 : 500,
                          textDecoration: fest ? "underline" : "none", textUnderlineOffset: 2,
                          borderRadius: 4, cursor: "pointer",
                          background: isSel ? "var(--accent-bg)"
                                    : iso === todayStr ? "var(--accent)"
                                    : fest ? "rgba(239,68,68,.10)"
                                    : finde ? "var(--bg)" : "transparent",
                          color: iso === todayStr ? "#fff"
                               : fest ? "var(--red-text)"
                               : finde ? "var(--text3)" : "var(--text2)",
                          border: isSel ? "1px solid var(--accent)" : "1px solid transparent",
                        }}>
                        <span style={{ lineHeight: 1 }}>{dia}</span>
                        {/* Franja de puntos: una sola fila para no romper la celda */}
                        {hayAlgo && (
                          <span style={{ display: "flex", gap: 2, height: 5, alignItems: "center" }}>
                            {dayCal.slice(0, 2).map((ev, i) => (
                              <span key={`c${i}`} style={{ width: 4.5, height: 4.5, borderRadius: 1,
                                background: ev.color || ev.calendario_color || "#94a3b8" }}/>
                            ))}
                            {nOut > 0 && <span style={{ width: 4.5, height: 4.5, borderRadius: "50%", background: "#0078d4" }}/>}
                            {nAg  > 0 && <span style={{ width: 3.5, height: 3.5, borderRadius: 0.5, background: "#8b5cf6", transform: "rotate(45deg)", margin: "0 1px" }}/>}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Legend ── */}
        <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text2)", flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#0078d4", display: "inline-block" }}/> Evento de Outlook (círculo)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 1, background: "#8b5cf6", display: "inline-block", transform: "rotate(45deg)", margin: 1 }}/> Entrada del Cuaderno (rombo)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: "#94a3b8", display: "inline-block" }}/> Calendario propio (cuadrado, con su color)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: "rgba(239,68,68,.5)", display: "inline-block" }}/> Festivo (número subrayado en rojo)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--bg)", border: "1px solid var(--border)", display: "inline-block" }}/> Fin de semana
          </span>
        </div>
      </div>

      {/* ── Day detail panel ── */}
      {selectedDay && (
        <aside style={{
          width: 280, flexShrink: 0, background: "var(--surface)",
          borderRadius: 12, border: "1px solid var(--border)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text)" }}>
                {selDate?.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
              </div>
              <div style={{ fontSize: 10.5, color: festivos[selStr] ? "var(--red-text)" : "var(--text3)", marginTop: 2 }}>
                {festivos[selStr] ? `Festivo · ${festivos[selStr]}`
                  : esFinde(selStr) ? "Fin de semana"
                  : [
                      selOutlook.length && `${selOutlook.length} evento${selOutlook.length !== 1 ? "s" : ""} Outlook`,
                      selCal.length     && `${selCal.length} en calendarios`,
                      selAgenda.length  && `${selAgenda.length} entrada${selAgenda.length !== 1 ? "s" : ""} cuaderno`,
                    ].filter(Boolean).join(" · ") || "Sin actividad"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
              {puedeCrear && (
                <button aria-label="Añadir evento este día" onClick={() => onNuevoEvento?.(selStr)} className="btn" title="Añadir evento este día"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", color: "var(--accent)" }}>
                  <Icon name="plus-lg" size={14} color="var(--accent)"/>
                </button>
              )}
              <button aria-label="Cerrar el detalle del día" onClick={() => setSelectedDay(null)} className="btn"
                style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex", background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", color: "var(--text3)" }}>
                <Icon name="x" size={14} color="var(--text3)"/>
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
            {selCal.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>
                  Calendarios
                </div>
                {selCal.map(ev => {
                  const col = ev.color || ev.calendario_color || "#94a3b8";
                  return (
                    <div key={ev.id} onClick={() => ev.can_edit && onEditarEvento?.(ev)}
                      role={ev.can_edit ? "button" : undefined} tabIndex={ev.can_edit ? 0 : undefined}
                      onKeyDown={e => { if (ev.can_edit && e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onEditarEvento?.(ev); } }}
                      style={{ padding: "8px 10px", borderRadius: 8, marginBottom: 4, background: "var(--bg)", border: `1px solid color-mix(in srgb, ${col} 45%, transparent)`, cursor: ev.can_edit ? "pointer" : "default" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.3, flex: 1 }}>{ev.titulo}</div>
                        {ev.anual && <Icon name="arrow-repeat" size={10} color="var(--text3)"/>}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 9.5, color: col, fontWeight: 600 }}>
                          {ev.all_day ? "Todo el día" : `${ev.hora_inicio}${ev.hora_fin ? ` – ${ev.hora_fin}` : ""}`}
                        </span>
                        <span style={{ fontSize: 9.5, color: "var(--text3)" }}>· {ev.calendario}</span>
                        {ev.tipo !== "evento" && <span style={{ fontSize: 9.5, color: "var(--text3)" }}>· {ev.tipo}</span>}
                      </div>
                      {ev.descripcion && (
                        <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4, lineHeight: 1.4 }}>{ev.descripcion}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {selOutlook.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>
                  Eventos Outlook
                </div>
                {selOutlook.map(e => {
                  const startTime = e.start_datetime ? new Date(e.start_datetime).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : null;
                  const endTime   = e.end_datetime   ? new Date(e.end_datetime).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : null;
                  return (
                    <div key={e.id || e.external_id}
                      style={{ padding: "8px 10px", borderRadius: 8, marginBottom: 4, background: "var(--bg)", border: "1px solid color-mix(in srgb, #0078d4 45%, transparent)" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.3 }}>{e.title}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                        {e.is_all_day ? <span style={{ fontSize: 9.5, color: "#0078d4", fontWeight: 600 }}>Todo el día</span>
                          : startTime && <span style={{ fontSize: 9.5, color: "#0078d4", fontWeight: 600 }}>{startTime}{endTime ? ` – ${endTime}` : ""}</span>}
                        {e.location && <span style={{ fontSize: 9.5, color: "var(--text3)" }}>· {e.location}</span>}
                        {e.web_link && <a href={e.web_link} target="_blank" rel="noreferrer" style={{ fontSize: 9.5, color: "var(--accent)" }}>Abrir</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {selAgenda.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8 }}>
                  Cuaderno
                </div>
                {selAgenda.map(a => (
                  <div key={a.id}
                    style={{
                      padding: "8px 10px", borderRadius: 8, marginBottom: 4,
                      background: "var(--bg)", border: "1px solid color-mix(in srgb, #8b5cf6 45%, transparent)",
                    }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", lineHeight: 1.3 }}>
                      {a.description || a.title || "Entrada"}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                      {a.type && <span style={{ fontSize: 9.5, color: "#8b5cf6", fontWeight: 600 }}>{a.type}</span>}
                      {a.hours && <span style={{ fontSize: 9.5, color: "var(--text3)" }}>{a.hours}h</span>}
                      {a.department && <span style={{ fontSize: 9.5, color: "var(--text3)" }}>· {a.department}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!selCal.length && !selOutlook.length && !selAgenda.length && (
              <div style={{ fontSize: 11.5, color: "var(--text3)", lineHeight: 1.5, padding: "8px 2px" }}>
                No hay nada este día.
                {puedeCrear && " Pulsa + para añadir un evento."}
              </div>
            )}
          </div>
        </aside>
      )}
      </div>{/* end flex row */}
    </div>
  );
}
